import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BUILDINGS as STATIC_BUILDINGS } from "./apartments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Ctx = createContext(null);

export const toAbsUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u.startsWith("/") ? "" : "/"}${u}`;
};

const defined = (v) => v !== undefined && v !== null;

function mergeApt(base, d) {
  if (!d) return base;
  const m = { ...base };
  if ("price" in d) m.price = d.price;
  if (defined(d.loggiaPrice)) m.loggiaPrice = d.loggiaPrice;
  if (defined(d.loggiaPricePerM2)) m.loggiaPricePerM2 = d.loggiaPricePerM2;
  if (defined(d.rooms)) m.rooms = d.rooms;
  if (defined(d.floor)) m.floor = d.floor;
  if (defined(d.floors)) m.floors = d.floors;
  if (defined(d.area)) m.area = d.area;
  if (m.area) m.pricePerM2 = Math.round(m.price / m.area);
  if (defined(d.terraceType)) m.terraceType = d.terraceType;
  if (defined(d.apartmentArea)) m.apartmentArea = d.apartmentArea;
  if (defined(d.terraceArea)) m.terraceArea = d.terraceArea;
  if (defined(d.loggiaArea)) m.loggiaArea = d.loggiaArea;
  if (defined(d.landShare)) m.landShare = d.landShare;
  if (defined(d.landShareNA)) m.landShareNA = d.landShareNA;
  if (defined(d.showPricePerM2)) m.showPricePerM2 = d.showPricePerM2;
  if (d.orientation && d.orientation.length) m.orientationKeys = d.orientation;
  m.factsVariant = m.terraceType === "loggia" ? "loggia" : (base.factsVariant ?? null);

  if (d.photos && d.photos.length) {
    const idx = defined(d.floorPlanIndex) ? d.floorPlanIndex : 0;
    const abs = d.photos.map(toAbsUrl);
    m.sheet = abs[idx] || abs[0] || "";
    m.photos = abs.filter((_, i) => i !== idx);
    m._dbPhotos = d.photos;
    m._floorPlanIndex = idx;
  }

  const so = {};
  ["orientation", "yearBuilt", "finishing", "buildingType", "heating", "energyClass",
   "features", "equipment", "security", "additionalSpaces", "terraceArea", "apartmentArea"]
    .forEach((k) => { if (defined(d[k])) so[k] = d[k]; });
  if (Object.keys(so).length) m._specOverride = so;
  return m;
}

function buildCustomBase(d) {
  return {
    code: d.code,
    building: d.building,
    unit: d.unit || d.code.split(" ").pop(),
    number: d.number ?? 999,
    floor: d.floor ?? 1,
    floors: d.floors ?? 5,
    rooms: d.rooms ?? 1,
    terraceType: d.terraceType ?? "terrace",
    terrace: null,
    area: d.area ?? 0,
    orientation: "—",
    price: d.price ?? 0,
    pricePerM2: d.area ? Math.round((d.price || 0) / d.area) : 0,
    defaultStatus: d.status || "Available",
    featured: false,
    apartmentArea: null,
    orientationKeys: [],
    terraceArea: null,
    landPlotArea: null,
    landShare: null,
    loggiaArea: null,
    factsVariant: null,
    sheet: "",
    photos: [],
    _custom: true,
  };
}

function buildMerged(detailsMap) {
  const out = {};
  const staticCodes = new Set();
  Object.entries(STATIC_BUILDINGS).forEach(([key, b]) => {
    out[key] = {
      ...b,
      apartments: b.apartments.map((a) => { staticCodes.add(a.code); return mergeApt(a, detailsMap[a.code]); }),
    };
  });
  // append custom (DB-only) units not present in static data
  Object.values(detailsMap).forEach((d) => {
    if (staticCodes.has(d.code)) return;
    const bKey = d.building;
    if (!out[bKey]) return;
    out[bKey] = { ...out[bKey], apartments: [...out[bKey].apartments, mergeApt(buildCustomBase(d), d)] };
  });
  Object.values(out).forEach((b) => b.apartments.sort((x, y) => (x.number || 0) - (y.number || 0)));
  return out;
}

export const ApartmentDataProvider = ({ children }) => {
  const [buildings, setBuildings] = useState(STATIC_BUILDINGS);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/apartments/details`);
      const map = {};
      (data || []).forEach((d) => (map[d.code] = d));
      setBuildings(buildMerged(map));
    } catch (e) {
      setBuildings(STATIC_BUILDINGS);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return <Ctx.Provider value={{ buildings, refresh }}>{children}</Ctx.Provider>;
};

export const useBuildings = () => {
  const ctx = useContext(Ctx);
  return ctx || { buildings: STATIC_BUILDINGS, refresh: () => {} };
};
